import React from "react"
import { Select } from "antd"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { TABLE_OPERATION_OPTIONS } from "features/Reporting/hooks/useOperatorManagement"

import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import { SelectOption } from "types/ui.types"

/**
 * Props for TableVariableSelector component
 */
export interface TableVariableSelectorProps {
  /** Available variable options */
  selectOptions: SelectOption[]
  /** Currently selected cross-tab variables */
  crossTabVariables: (string | null)[]
  /** Selected statistical operators */
  selectedOperators: STATISTIC_TYPE[]
  /** Available operator options */
  availableOperatorOptions: Array<{ label: string; value: STATISTIC_TYPE }>
  /** Table operation type (XTAB vs DTAB) */
  tableOperationType: TABLE_OPERATION_TYPE
  /** Whether query instance is available */
  queryInstance: boolean
  /** Loading state */
  isLoading: boolean
  /** Queued for execution state */
  isQueuedForExecution: boolean
  /** Variable selection handler */
  onVariableSelect: (value: string, index: number) => void
  /** Operator change handler */
  onOperatorChange: (operators: STATISTIC_TYPE[]) => void
  /** Table operation type change handler */
  onTableOperationTypeChange: (operationType: TABLE_OPERATION_TYPE) => void
  /** Generate table handler */
  onGenerate: () => void
}

/**
 * Table Variable Selector Component
 *
 * Provides a comprehensive interface for table configuration:
 * - Operation type selection (XTAB vs DTAB with descriptions)
 * - Two-variable cross-tabulation selection with search
 * - Multiple statistical operator selection
 * - Generate button with validation
 *
 * Features:
 * - Responsive grid layout
 * - Searchable dropdowns with filtering
 * - Operation type descriptions for user guidance
 * - Validation feedback for incomplete configurations
 * - Loading states and queue management
 *
 * @param props - Component configuration
 * @returns JSX element for table variable selection
 */
const TableVariableSelector: React.FC<TableVariableSelectorProps> = ({
  selectOptions,
  crossTabVariables,
  selectedOperators,
  availableOperatorOptions,
  tableOperationType,
  queryInstance,
  isLoading,
  isQueuedForExecution,
  onVariableSelect,
  onOperatorChange,
  onTableOperationTypeChange,
  onGenerate,
}) => {
  return (
    <div className="p-2 bg-gray-50 bg-opacity-80 rounded-md shadow-sm">
      <div className="flex flex-col space-y-1.5">
        {/* Operation Type Selection */}
        <div>
          <Select
            placeholder="Select operation type"
            className="w-full text-xs"
            value={tableOperationType}
            onChange={onTableOperationTypeChange}
            size="small"
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px", minWidth: "250px" }}
            optionLabelProp="label"
          >
            {TABLE_OPERATION_OPTIONS.map((opt) => (
              <Select.Option
                key={opt.value}
                value={opt.value}
                label={opt.label}
              >
                <div>
                  <div style={{ fontWeight: "500" }}>{opt.label}</div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#666",
                      marginTop: "2px",
                    }}
                  >
                    {opt.description}
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Variable Selection Grid */}
        <div className="grid grid-cols-1 gap-1">
          <div className="col-span-1">
            <Select
              showSearch
              placeholder="First variable"
              optionFilterProp="label"
              className="w-full text-xs"
              options={selectOptions}
              value={crossTabVariables[0]}
              onChange={(v: string) => onVariableSelect(v, 0)}
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase()) ??
                false
              }
              size="small"
              style={{ fontSize: "11px" }}
              dropdownStyle={{ fontSize: "11px" }}
            />
          </div>
          <div className="col-span-1">
            <Select
              showSearch
              placeholder="Second variable"
              optionFilterProp="label"
              className="w-full text-xs"
              options={selectOptions}
              value={crossTabVariables[1]}
              onChange={(v: string) => onVariableSelect(v, 1)}
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase()) ??
                false
              }
              size="small"
              style={{ fontSize: "11px" }}
              dropdownStyle={{ fontSize: "11px" }}
            />
          </div>
        </div>

        {/* Operators and Generate Button */}
        <div className="flex items-center space-x-1">
          <div className="flex-grow">
            <Select
              mode="multiple"
              placeholder="Operators"
              className="w-full text-xs"
              options={availableOperatorOptions}
              value={selectedOperators}
              onChange={onOperatorChange}
              size="small"
              maxTagCount={2}
              style={{ fontSize: "11px" }}
              dropdownStyle={{ fontSize: "11px" }}
            />
          </div>
          <div>
            <DataQButton
              isDisabled={
                !queryInstance ||
                crossTabVariables.length !== 2 ||
                isLoading ||
                isQueuedForExecution
              }
              isLoading={isLoading || isQueuedForExecution}
              onClick={onGenerate}
              type="primary"
              size="small"
            >
              {isQueuedForExecution ? "Queued..." : "Run"}
            </DataQButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TableVariableSelector
